/** The phone's menu over the page: its state, and what holds the page still and keeps the focus in it. */
import { useEffect, useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import { useMedia } from "../lib/media.ts";
import { lockScroll, trapTab } from "../lib/overlay.ts";

/** Wide enough for the sidebar beside the page (Tailwind's `md`): no phone bar, no menu over the page. */
const WIDE = "(min-width: 48rem)";

/** Padding that keeps clear of the screen's edges where they're not safe (a notch, rounded corners), at least 0.75rem. */
export const SAFE_X =
  "max-md:pr-[max(0.75rem,env(safe-area-inset-right,0px))] max-md:pl-[max(0.75rem,env(safe-area-inset-left,0px))]";
const SAFE_TOP = "max-md:pt-[max(0.75rem,env(safe-area-inset-top,0px))]";
export const SAFE_BOTTOM = "max-md:pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]";

/**
 * The menu over the page, on a phone: fixed over everything but toasts, sliding in quickly from the left and fading
 * out (neither with reduced motion). Wide, these classes do nothing: the sidebar is beside the page.
 */
const PANEL = `max-md:fixed max-md:inset-0 max-md:z-40 max-md:flex max-md:flex-col max-md:bg-zinc-100 ${SAFE_TOP} ${SAFE_X} max-md:pb-0`;
const PANEL_OPEN =
  "max-md:visible max-md:[--edge:-1] max-md:motion-safe:animate-[slide-in_200ms_cubic-bezier(0.32,0.72,0,1)_both]";
const PANEL_CLOSED =
  "max-md:invisible max-md:opacity-0 max-md:transition-[opacity,visibility] max-md:duration-150 max-md:ease-out motion-reduce:transition-none";

/** Room atop the page for the phone's bar, when scrolling to something. */
const BAR_ROOM = "max-md:scroll-pt-[4.25rem]";

/** The menu's state: open (on a phone only), and the ids its parts point at each other with. */
export interface Drawer {
  /** Open, on a phone. */
  open: boolean;
  /** The menu button's id (the focus goes back to it). */
  toggleId: string;
  /** The ✕'s id (the focus goes to it on opening). */
  closeId: string;
  /** Its panel's id (what the menu button opens). */
  panelId: string;
  /** Its panel's classes, open or closed. */
  panelClass: string;
  show: () => void;
  /** Closes it; `away`: going somewhere from it (the page there starts at its top). */
  close: (away?: boolean) => void;
  /** What makes its panel a dialog while it's open over the page. */
  dialog: { role?: "dialog"; "aria-modal"?: boolean; "aria-label"?: string };
}

/**
 * The phone's menu: while open, the page under it doesn't scroll (and is back where it was after, or at the top of
 * where a link went), Esc closes it, Tab stays inside, and closing gives the focus back to the menu button. Growing
 * wide closes it.
 */
export function useDrawer(ids: { toggleId: string; closeId: string; panelId: string }): Drawer {
  const [shown, setShown] = useState(false);
  const wide = useMedia(WIDE);
  const open = shown && !wide;
  // Whether it closed going somewhere (kept for its closing, not drawn).
  const [away] = useState(() => ({ current: false }));
  // The bar stays atop the screen on a phone: what's scrolled to (an anchor, a setting) stops below it.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add(BAR_ROOM);
    return () => html.classList.remove(BAR_ROOM);
  }, []);
  useEffect(() => {
    if (wide) {
      setShown(false);
    }
  }, [wide]);
  const close = (to = false) => {
    away.current = to;
    setShown(false);
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: opening and closing it is what this follows; ids stay.
  useEffect(() => {
    if (!open) {
      return;
    }
    away.current = false;
    const unlock = lockScroll();
    document.getElementById(ids.closeId)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !document.querySelector("dialog[open]")) {
        e.preventDefault();
        close();
      } else {
        const panel = document.getElementById(ids.panelId);
        if (panel) {
          trapTab(panel, e);
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlock(away.current ? 0 : undefined);
      // Where the page is: not scrolled to the menu button (atop it).
      document.getElementById(ids.toggleId)?.focus({ preventScroll: true });
    };
  }, [open]);
  return {
    open,
    ...ids,
    panelClass: `${PANEL} ${open ? PANEL_OPEN : PANEL_CLOSED}`,
    show: () => setShown(true),
    close,
    dialog: open ? { role: "dialog", "aria-modal": true, "aria-label": t("nav.menu") } : {},
  };
}
